import axios from "axios";
import authService from "../services/authService";
import { API_CONFIG } from "../config/api.config";

const API_BASE_URL = API_CONFIG.BASE_URL;

// Create axios instance with interceptors for authentication
const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor for authentication
axiosInstance.interceptors.request.use(
  (config) => {
    const token = authService.getToken();
    
    if (token) {
      const isExpired = authService.isTokenExpired(token);
      
      if (!isExpired) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor for error handling
axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    // Only logout on 401 if it's truly an authentication error
    // Don't logout on other errors - let the calling function handle them
    if (error.response?.status === 401 && error.response?.data?.message?.includes('token')) {
      const token = authService.getToken();
      if (!token || authService.isTokenExpired(token)) {
        authService.logout();
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

const profileApi = {
  // Get basic profile information
  getBasicProfile: async (userId) => {
    try {
      const response = await axiosInstance.get(
        `/profile/basic/${userId}`
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching basic profile:", error);
      throw error;
    }
  },

  // Get booking statistics
  getBookingStats: async (userId) => {
    try {
      const response = await axiosInstance.get(
        `/profile/bookings/${userId}`
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching booking stats:", error);
      throw error;
    }
  },

  // Get all user bookings (restaurant + room bookings)
  getAllUserBookings: async (userId) => {
    try {
      const response = await axiosInstance.get(
        `/profile/all-bookings/${userId}`
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching all user bookings:", error);
      throw error;
    }
  },

  // Get payment methods and summary
  getPaymentDetails: async (userId) => {
    try {
      const response = await axiosInstance.get(
        `/profile/payments/${userId}`
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching payment details:", error);
      throw error;
    }
  },

  // Get user preferences
  getPreferences: async (userId) => {
    try {
      const response = await axiosInstance.get(
        `/profile/preferences/${userId}`
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching preferences:", error);
      throw error;
    }
  },

  // Get subscription information
  getSubscriptionInfo: async (userId) => {
    try {
      const response = await axiosInstance.get(
        `/profile/subscription/${userId}`
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching subscription info:", error);
      throw error;
    }
  },

  // Get user settings
  getSettings: async (userId) => {
    try {
      const response = await axiosInstance.get(
        `/profile/settings/${userId}`
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching settings:", error);
      throw error;
    }
  },

  // Upload profile picture
  uploadProfilePicture: async (userId, file) => {
    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await axiosInstance.post(
        `/profile/upload-picture/${userId}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error uploading profile picture:", error);
      throw error;
    }
  },

  // Get user settings
  getUserSettings: async (userId) => {
    try {
      const response = await axiosInstance.get(
        `/profile/settings/${userId}`
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching user settings:", error);
      throw error;
    }
  },

  // Update basic profile
  updateBasicProfile: async (userId, profileData) => {
    try {
      const response = await axiosInstance.put(
        `/profile/basic/${userId}`,
        profileData
      );
      return response.data;
    } catch (error) {
      console.error("Error updating basic profile:", error);
      throw error;
    }
  },

  // Update settings
  updateSettings: async (userId, settingsData) => {
    try {
      const response = await axiosInstance.put(
        `/profile/settings/${userId}`,
        settingsData
      );
      return response.data;
    } catch (error) {
      console.error("Error updating settings:", error);
      throw error;
    }
  },

  // Update preferences
  updatePreferences: async (userId, preferencesData) => {
    try {
      const response = await axiosInstance.put(
        `/profile/preferences/${userId}`,
        preferencesData
      );
      return response.data;
    } catch (error) {
      console.error("Error updating preferences:", error);
      throw error;
    }
  },

  // Get complete profile data
  getCompleteProfile: async (userId) => {
    try {
      const [
        basicProfile,
        bookingStats,
        paymentDetails,
        preferences,
        subscriptionInfo,
        settings,
      ] = await Promise.all([
        profileApi.getBasicProfile(userId),
        profileApi.getBookingStats(userId),
        profileApi.getPaymentDetails(userId),
        profileApi.getPreferences(userId),
        profileApi.getSubscriptionInfo(userId),
        profileApi.getSettings(userId),
      ]);

      return {
        basic: basicProfile,
        bookings: bookingStats,
        payments: paymentDetails,
        preferences: preferences,
        subscription: subscriptionInfo,
        settings: settings,
      };
    } catch (error) {
      console.error("Error fetching complete profile:", error);
      throw error;
    }
  },

  // Change password
  changePassword: async (userId, passwordData) => {
    try {
      const response = await axiosInstance.put(
        `/profile/change-password/${userId}`,
        passwordData
      );
      return response.data;
    } catch (error) {
      console.error("Error changing password:", error);
      throw error;
    }
  },

  // Export user data
  exportUserData: async (userId, params = {}) => {
    try {
      const response = await axiosInstance.get(
        `/profile/export-data/${userId}`,
        {
          params: { ...params, _t: Date.now() }, // Add timestamp to bypass cache
          headers: {
            "Cache-Control": "no-cache",
            Pragma: "no-cache",
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error exporting user data:", error);
      throw error;
    }
  },
};

export default profileApi;
